/**
 * Thin fetch wrapper. Adds the bearer token, surfaces server errors as
 * `ApiError`, and clears the token from localStorage on 401 so the token gate
 * re-prompts.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const TOKEN_KEY = 'hb_token';

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

const tokenRejectedListeners = new Set<() => void>();

export function onTokenRejected(handler: () => void): () => void {
  tokenRejectedListeners.add(handler);
  return () => tokenRejectedListeners.delete(handler);
}

function emitTokenRejected(): void {
  for (const handler of tokenRejectedListeners) handler();
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });

  if (response.status === 401) {
    clearToken();
    emitTokenRejected();
    throw new ApiError('UNAUTHORIZED', 'Session expired', 401);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    throw new ApiError(
      body.error?.code ?? 'UNKNOWN',
      body.error?.message ?? `HTTP ${response.status}`,
      response.status,
      body.error?.details,
    );
  }

  return (await response.json()) as T;
}
